import { get } from 'lodash-es';

import type { VTreeNode, VTreeNodeList } from './type';

export const eachDeep = ({
  nodeList,
  parentNode,
  callback,
}: {
  nodeList: VTreeNodeList;
  parentNode?: Nullish<VTreeNode>;
  callback: (
    node: VTreeNode,
    index: number,
    parent: Nullish<VTreeNode>,
  ) => void;
}) => {
  nodeList.forEach((node, index) => {
    callback(node, index, parentNode);

    node.children?.length &&
      eachDeep({ nodeList: node.children, parentNode: node, callback });
  });
};

const makeVTreeNode = <T extends Dictionary>(
  item: T,
  keyValMap: {
    children: keyof T;
    id: keyof T;
    label: keyof T;
    disabled?: string;
    expanded?: string;
    selected?: string;
  },
): VTreeNode & { native: T } => {
  return {
    children: [] as (VTreeNode & { native: T })[],
    id: get(item, keyValMap.id),
    label: get(item, keyValMap.label),
    disabled: keyValMap.disabled ? get(item, keyValMap.disabled) : false,
    expanded: keyValMap.expanded ? get(item, keyValMap.expanded) : false,
    selected: keyValMap.selected ? get(item, keyValMap.selected) : false,
    native: item,
  };
};

export const getVTreeNodeList =
  (nodeById: Dictionary<VTreeNode>) => (idList: VTreeNode['id'][]) =>
    idList.map(id => nodeById[id]);

export const makeVTreeNodeList = <T extends Dictionary>(
  list: T[],
  keyValMap: {
    children: keyof T;
    id: keyof T;
    label: keyof T;
    disabled?: string;
    expanded?: string;
    selected?: string;
  },
  condition: (item: T) => boolean = (_: T) => true,
) => {
  const nodeList: (VTreeNode & { native: T })[] = [];
  const dfs = (item: T) => {
    if (condition(item)) {
      const node = makeVTreeNode(item, keyValMap);
      const children = (get(item, keyValMap.children) ?? []) as T[];

      if (children.length) {
        const nodeChildren = [] as (VTreeNode & { native: T })[];

        children.forEach(item => {
          const result = dfs(item);

          result ? nodeChildren.push(result) : void 0;
        });

        node.children = nodeChildren;
      }

      return node;
    }

    return false;
  };

  list.forEach(item => {
    const result = dfs(item);

    result ? nodeList.push(result) : void 0;
  });

  return nodeList;
};
